'use client';

import { useState, useEffect, useRef } from 'react';
import { Check, Download, Camera } from 'lucide-react';
import { formatPriceCents } from '@/lib/utils/currency';
import { useIsMobile } from '@/hooks/use-mobile';
import { photoRequestHeaders } from '@/lib/race-photos/client-id';
import {
  planDownload,
  downloadButtonLabel,
  formatBytes,
  MAX_SHARE_PHOTOS,
} from '@/lib/race-photos/download-plan';
import { mapWithConcurrency } from '@/lib/race-photos/fetch-pool';
import { DOWNLOAD_TOKEN_TTL_DAYS } from '@/lib/race-photos/download-access';
import { HubSkeleton, hubButtonStyle } from '@/components/hub/hub-ui';
import {
  PhotoViewer,
  StepError,
  StepHeading,
  StepNote,
  StepText,
  TileTick,
  stepSecondaryStyle,
  viewerActionStyle,
  type PhotoVariant,
} from './photo-flow-ui';

/** Several at once, so the set is ready before a thumb reaches the button. */
const PREFETCH_CONCURRENCY = 5;

// Web Share API with files: supported on iOS/Android, where it opens the
// native share sheet with "Save Image" straight to the camera roll.
function canShareFiles(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({ files: [new File([''], 'photo.jpg', { type: 'image/jpeg' })] });
  } catch {
    return false;
  }
}

interface PreviewItem {
  id: string;
  previewUrl: string | null;
  filename: string;
}

interface ThemeColors {
  primary: string;
  secondary: string;
  text: string;
  textMuted: string;
  buttonText?: string;
}

interface PhotoResultsGridProps {
  albumId: string;
  /**
   * Where this grid's download endpoints live. An album link passes
   * `/api/race-photos/<albumId>`; the event's master link passes
   * `/api/race-photos/event/<token>`. Defaults to the album form so existing
   * callers are unchanged.
   */
  apiBase?: string;
  matchedPhotoIds: string[];
  previews?: PreviewItem[];
  downloadToken: string | null;
  participantPrice?: number;
  participantPriceCurrency?: string;
  onRequestDownload: () => void;
  onRetakeSelfie?: () => void;
  themeColors?: ThemeColors;
  /**
   * Photos that matched, but below the confident threshold. Shown separately
   * and included only when the athlete ticks them: at 85% similarity we are
   * genuinely unsure, and they are the only person who can settle it.
   */
  possibleMatches?: PreviewItem[];
  pickedPossible?: Set<string>;
  onTogglePossible?: (id: string) => void;
  /** 'hub' for the athlete hub's look (photo-flow-ui.tsx). */
  variant?: PhotoVariant;
  /** Already paid for (a pre-purchase): the call to action claims, not buys. */
  prePaid?: boolean;
  /** Unlocking is in flight (a free download or a redemption). */
  unlocking?: boolean;
  /**
   * The download endpoint refused this token (403), as found by the caller
   * (RacePhotoFinder loads the same list for an emailed link). The grid also
   * finds it on its own fetch; either way it says so (DownloadLinkRefused).
   */
  linkRefused?: boolean;
}

interface PhotoItem {
  id: string;
  signedUrl: string;
  filename: string;
  width?: number;
  height?: number;
  /** Bytes, so the grid can warn before a large download on mobile data. */
  fileSize?: number;
}

function formatPrice(cents: number, currency: string) {
  return formatPriceCents(cents, currency);
}

export function PhotoResultsGrid({ albumId, apiBase, matchedPhotoIds, previews = [], downloadToken, participantPrice = 0, participantPriceCurrency = 'eur', onRequestDownload, onRetakeSelfie, possibleMatches = [], pickedPossible, onTogglePossible, variant = 'page', prePaid = false, unlocking = false, linkRefused = false }: PhotoResultsGridProps) {
  // One base for every download call, so album and master links share this grid
  // instead of it knowing which scope it is inside.
  const base = apiBase ?? `/api/race-photos/${albumId}`;
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  /**
   * Why the purchased photos could not be listed: the token was refused (403:
   * expired, refunded, or for another link), or the request failed. Both used
   * to fall through to "0 photos found", which reads as "you are in none of
   * the photos" to someone who paid for them.
   */
  const [listError, setListError] = useState<'refused' | 'failed' | null>(null);
  const [listAttempt, setListAttempt] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<string | null>(null);
  /**
   * Photos fetched and waiting for the tap that hands them to the share sheet.
   * Null means we have not prepared them yet, so the button reads "Save all".
   */
  const [readyFiles, setReadyFiles] = useState<File[] | null>(null);
  /** Why the last download attempt failed, shown rather than swallowed. */
  const [downloadError, setDownloadError] = useState<string | null>(null);
  /**
   * A zip has been handed to the browser. Shown because a large archive takes
   * time to begin, and silence is what made people tap the button again.
   */
  const [zipStarted, setZipStarted] = useState(false);
  /** Briefly true after a zip is handed over, purely so the button reacts. */
  const [startingZip, setStartingZip] = useState(false);
  /**
   * The share sheet is not going to work for this set, so the next tap has to
   * be a zip instead. Held as state rather than acted on immediately BECAUSE
   * we only ever learn this after awaiting the photo fetches, and by then iOS
   * has withdrawn the permission the original tap carried. See prepareShare.
   */
  const [shareUnavailable, setShareUnavailable] = useState<string | null>(null);
  /**
   * The background fetch, so a tap that arrives mid-flight waits for THIS
   * rather than starting a second copy of the same downloads.
   */
  const prefetchRef = useRef<Promise<File[]> | null>(null);
  /**
   * True only when the athlete tapped before the background fetch had
   * finished, so they are genuinely waiting on a second tap. When the prefetch
   * wins the race, which is the normal case, the button keeps saying "Save
   * all" and that one tap opens the sheet.
   */
  const [awaitingSecondTap, setAwaitingSecondTap] = useState(false);
  /** The background fetch is running, so the button says so. */
  const [warming, setWarming] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  /**
   * The maybes have their own viewer. Deciding "is this me" from a tile is the
   * whole task here, and it is the one thing the tile cannot do.
   */
  const [possibleIndex, setPossibleIndex] = useState<number | null>(null);
  const isMobile = useIsMobile();

  const hasAccess = !!downloadToken;
  const canShare = isMobile && canShareFiles();
  /**
   * What this set will ACTUALLY do, decided once. The button's label is derived
   * from the same answer, so the two cannot promise different things again: a
   * 103-photo set on an iPhone offered "Save all" and then delivered a zip.
   */
  const totalBytes = photos.reduce((sum, p) => sum + (p.fileSize || 0), 0);
  const plan = planDownload({
    canShare,
    photoCount: photos.length,
    totalBytes,
    shareRefused: !!shareUnavailable,
  });
  const willShare = plan === 'share';

  // Same-origin stream with a real filename and attachment disposition, so
  // downloads never land the user on a raw S3 URL.
  const photoDownloadUrl = (photoId: string) =>
    `${base}/download/photo?token=${downloadToken}&photoId=${encodeURIComponent(photoId)}`;

  const fetchPhotoFile = async (photo: PhotoItem): Promise<File> => {
    const res = await fetch(photoDownloadUrl(photo.id), { headers: photoRequestHeaders(false) });
    if (res.status === 403) setListError('refused');
    if (!res.ok) {
      const detail = await res.json().catch(() => null);
      throw new Error(detail?.message || `Failed to fetch photo ${photo.id}`);
    }
    const blob = await res.blob();
    return new File([blob], photo.filename || 'photo.jpg', { type: blob.type || 'image/jpeg' });
  };

  const triggerDirectDownload = (photo: PhotoItem) => {
    const a = document.createElement('a');
    a.href = photoDownloadUrl(photo.id);
    a.download = photo.filename || 'photo.jpg';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // When download token is available, fetch signed URLs for originals
  useEffect(() => {
    if (!downloadToken) return;

    const fetchPhotos = async () => {
      setLoading(true);
      setListError(null);
      try {
        const res = await fetch(`${base}/download?token=${downloadToken}`, { headers: photoRequestHeaders(false) });
        if (res.status === 403) {
          setListError('refused');
        } else if (!res.ok) {
          setListError('failed');
        } else {
          const data = await res.json();
          setPhotos(data.photos);
          setSelectedIds(new Set(data.photos.map((p: PhotoItem) => p.id)));
          // A different set of photos makes anything already prepared for the
          // share sheet stale, and handing iOS the previous set would quietly
          // save the wrong pictures.
          setReadyFiles(null);
          setDownloadError(null);
          setShareUnavailable(null);
          setAwaitingSecondTap(false);
        }
      } catch (error) {
        console.error('Failed to fetch photos:', error);
        setListError('failed');
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, [albumId, downloadToken, listAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === matchedPhotoIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(matchedPhotoIds));
    }
  };

  /**
   * The ZIP, handed to the browser's own download manager rather than pulled
   * into memory first.
   *
   * This used to `fetch()` then `await res.blob()`, which buffers the ENTIRE
   * archive in the tab before writing a single byte to disk. At this event the
   * biggest purchase is 162 photos, about 280MB, and asking a phone to hold
   * that in memory is how you get a tab that dies rather than a file that
   * saves. A plain navigation to an attachment URL streams straight to storage,
   * shows the browser's own progress, and has no such ceiling.
   *
   * The trade is that we can no longer read the status code, so a refusal shows
   * as the browser opening our error JSON instead of a tidy banner. That is a
   * fair trade now: the token was already proved good by the /download call
   * that populated this grid, and the rate limit that used to refuse these is
   * gone. A download that cannot start is now much rarer than a download too
   * big to hold.
   */
  const handleDownloadZip = () => {
    if (!downloadToken) return;
    setDownloadError(null);
    const a = document.createElement('a');
    a.href = `${base}/download/zip?token=${downloadToken}`;
    a.download = 'my-race-photos.zip';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Big archives take a while to begin. Without this the athlete taps, sees
    // nothing happen for twenty seconds, and taps again, which is the whole
    // failure pattern this PR exists to end.
    setZipStarted(true);
    // A navigation download tells us nothing about its progress, so the button
    // cannot track it. It can at least stop looking idle while the server is
    // opening the first of a hundred photos, which is the window in which
    // people concluded nothing had happened and tapped again.
    setStartingZip(true);
    window.setTimeout(() => setStartingZip(false), 6000);
  };

  /**
   * One photo. On a phone this opens the native share sheet, where "Save
   * Image" puts it straight in the camera roll; everywhere else it is an
   * ordinary download.
   *
   * The same activation rule as prepareShare applies, in miniature. One photo
   * is usually fetched well inside the window that lets us act on the tap, so
   * the share normally succeeds, but if it does not we must not answer with a
   * programmatic download click: that is the call a browser silently ignores
   * once the tap has expired, and silence is how this whole class of bug hid.
   * We say so instead, and the next tap works, because by then the photo is
   * cached and the direct download happens inside a fresh tap.
   */
  const handleDownloadSingle = async (photo: PhotoItem) => {
    if (!canShare) {
      // No await before this, so it is still inside the tap: a plain download.
      triggerDirectDownload(photo);
      return;
    }
    try {
      const file = await fetchPhotoFile(photo);
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
        return;
      }
      setDownloadError('Your phone would not open the save sheet. Tap the photo again to download it.');
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') return; // user closed the sheet
      console.error('Share failed:', error);
      setDownloadError('Could not save that photo. Tap it again to try downloading it.');
    }
  };

  /**
   * Mobile "save all", in two taps rather than one.
   *
   *   tap 1                        tap 2
   *   ─────                        ─────
   *   [ Save all ]  ──fetch N──►  [ Save 12 photos ] ──► iOS share sheet
   *                  (progress)                          "Save 12 Images"
   *
   * The second tap is not ceremony, it is the whole fix. iOS only opens a share
   * sheet while the tap that asked for it is still "live", and that window is a
   * few seconds. Fetching a dozen full-size race photos over venue wifi takes
   * longer than that, so the old one-tap version reliably lost the window and
   * iOS refused the sheet. Nothing visible happened, so people tapped again,
   * and the second tap often DID work, because the photos were now cached and
   * the fetch finished inside the window. That is exactly the "doesn't register
   * until you press it a couple of times" the organisers reported.
   *
   * So: tap one does the slow part and shows progress. Tap two calls share
   * immediately, with the files already in hand, inside its own fresh gesture.
   */
  /**
   * Fetch the photos BEFORE the athlete asks for them.
   *
   * This is the whole answer to "why do I have to press it twice". iOS only
   * opens the share sheet while the tap that asked for it is still live, about
   * five seconds, and fetching a dozen full-size photos takes longer than
   * that. Nothing can be done about the rule, so we move the work to before
   * the tap instead: by the time a thumb reaches the button the files are in
   * hand, and share() is called straight from the tap with nothing awaited in
   * between. One press, sheet opens.
   *
   * Only for sets planDownload has already routed to the share sheet, which
   * is what bounds the data spent on somebody's behalf: anything heavier is a
   * zip and fetches nothing ahead. Failure here is silent on purpose, because
   * nobody has asked for it yet and the tap path still tries and still speaks
   * up if it fails there.
   */
  useEffect(() => {
    // No separate size test here: planDownload has already decided this set is
    // small enough for the share sheet, and anything heavier is a zip, which
    // needs nothing fetched ahead because it streams straight to storage.
    if (plan !== 'share' || readyFiles || shareUnavailable) return;
    if (prefetchRef.current) return;

    const controller = new AbortController();
    setWarming(true);
    const run = mapWithConcurrency(photos, {
      concurrency: PREFETCH_CONCURRENCY,
      signal: controller.signal,
      worker: (photo) => fetchPhotoFile(photo),
    });
    prefetchRef.current = run;

    run
      .then((files) => {
        if (controller.signal.aborted) return;
        setWarming(false);
        // canShare has to be asked with the real files, and it is the question
        // that fails late on iOS. Asking now means a refusal is known before
        // the athlete taps, so the button can already read "Download all".
        if (!navigator.canShare({ files })) {
          setShareUnavailable('Your phone will not add this many photos at once.');
          return;
        }
        setReadyFiles(files);
      })
      .catch(() => {
        // Nobody is waiting on this. The tap path will try again and speak up
        // if it fails there.
        setWarming(false);
        prefetchRef.current = null;
      });

    return () => {
      controller.abort();
      setWarming(false);
      prefetchRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos, plan, totalBytes]);

  const prepareShare = async () => {
    if (!downloadToken) return;
    if (photos.length === 0) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      // Join the background fetch if one is running, rather than downloading
      // everything a second time. Usually it is already finished and this
      // resolves at once.
      const files = await (prefetchRef.current ?? mapWithConcurrency(photos, {
        concurrency: PREFETCH_CONCURRENCY,
        onProgress: (done, total) => setDownloadProgress(`Preparing ${done} of ${total}...`),
        worker: (photo) => fetchPhotoFile(photo),
      }));
      // iOS refuses large multi-file shares, and it only tells us once it has
      // the actual files, which is after every fetch above. So this can fail
      // late, and what happens next is the whole point of this function.
      if (!navigator.canShare({ files })) {
        setShareUnavailable('Your phone will not add this many photos at once.');
        return;
      }
      setReadyFiles(files);
      setAwaitingSecondTap(true);
    } catch (error) {
      console.error('Preparing photos failed:', error);
      setShareUnavailable('We could not get all your photos ready to save.');
    } finally {
      setDownloadProgress(null);
      setDownloading(false);
    }
  };

  /**
   * Tap two. Deliberately NOT async before the share call: the first await here
   * would end the user activation that makes the sheet legal.
   */
  const shareReadyFiles = () => {
    if (!readyFiles) return;
    setDownloadError(null);
    navigator
      .share({ files: readyFiles })
      .then(() => { setReadyFiles(null); setAwaitingSecondTap(false); })
      .catch((error: Error) => {
        // The athlete closing the sheet is a decision, not a failure: leave the
        // files ready so the button still works if they meant to keep them.
        if (error?.name === 'AbortError') return;
        console.error('Share failed:', error);
        // Again: offer the zip, never start it from here. This callback runs
        // after the share promise settles, which is outside the tap.
        setReadyFiles(null);
        setShareUnavailable('Your phone would not open the save sheet.');
      });
  };

  // Get image URL for a photo by index in matchedPhotoIds
  const getImageUrl = (index: number): string | null => {
    const photoId = matchedPhotoIds[index];
    const photo = photos.find(p => p.id === photoId);
    const preview = previews.find(p => p.id === photoId);
    return photo?.signedUrl || preview?.previewUrl || null;
  };

  const getFilename = (index: number): string => {
    const photoId = matchedPhotoIds[index];
    const photo = photos.find(p => p.id === photoId);
    const preview = previews.find(p => p.id === photoId);
    return photo?.filename || preview?.filename || 'Race photo';
  };

  /**
   * The maybes. Deliberately below the confident results and visually distinct,
   * because the ask is different: these are not "your photos", they are "is
   * this you?". Nothing here is bought unless it is ticked.
   */
  const possiblePanel = possibleMatches.length > 0 && onTogglePossible ? (
    <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--fvd-hairline-light)' }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--fv-photo-heading, var(--fvd-ink))', margin: 0 }}>
        Might also be you
      </h3>
      <p style={{ fontSize: 13, color: 'var(--fvd-ink-secondary)', lineHeight: 1.55, margin: '2px 0 14px', maxWidth: '52ch' }}>
        These look close, but we are not sure enough to add them for you. Tap one to see
        it properly, and tick any that are you.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
        {possibleMatches.map((photo, index) => {
          const picked = !!pickedPossible?.has(photo.id);
          return (
            <div key={photo.id} style={{ position: 'relative', aspectRatio: '4/3' }}>
              <button
                type="button"
                // Opens the viewer rather than selecting. At 140px nobody can
                // tell whether a face at the far side of an obstacle is theirs,
                // which is exactly the question this panel asks.
                onClick={() => setPossibleIndex(index)}
                aria-label={`Look at ${photo.filename || 'this photo'} properly`}
                style={{
                  width: '100%', height: '100%', padding: 0, cursor: 'pointer', lineHeight: 0,
                  display: 'block', borderRadius: 'var(--fv-radius-tile, 8px)', overflow: 'hidden', background: 'var(--fvd-neutral-chip)', border: 'none',
                  boxShadow: picked ? '0 0 0 2px var(--fvd-teal)' : 'inset 0 0 0 1px var(--fvd-hairline)',
                }}
              >
                {photo.previewUrl && (
                  <img
                    src={photo.previewUrl}
                    alt={photo.filename || 'Possible match'}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: picked ? 1 : 0.75 }}
                  />
                )}
              </button>
              <button
                type="button"
                onClick={() => onTogglePossible(photo.id)}
                aria-pressed={picked}
                aria-label={picked ? 'Remove from your photos' : 'This is me'}
                style={{
                  position: 'absolute', top: 0, left: 0, width: 40, height: 40,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                }}
              >
                <TileTick on={picked}><Check size={14} strokeWidth={2.5} /></TileTick>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  if (hasAccess && (linkRefused || listError === 'refused')) {
    return <DownloadLinkRefused variant={variant} isMobile={isMobile} onSearchAgain={onRetakeSelfie} />;
  }

  // Holding a link whose photos have arrived and number none: say so, rather
  // than "Your photos (0)" over a Deselect all and a Download all that do nothing.
  const ownedEmpty = hasAccess && !loading && !listError && photos.length === 0 && matchedPhotoIds.length === 0 && possibleMatches.length === 0;
  // The first fetch of an emailed or bought link, before any tile is known.
  const firstLoad = hasAccess && loading && matchedPhotoIds.length === 0;

  const watermark = (size: number, rows: number) => (
    <div aria-hidden="true" style={{
      position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 'inherit',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      pointerEvents: 'none',
    }}>
      <div style={{
        position: 'absolute', width: '200%', textAlign: 'center', transform: 'rotate(-30deg)',
        fontSize: size, fontWeight: 600, color: 'rgba(255,255,255,0.45)',
        textShadow: '0 1px 3px rgba(0,0,0,0.4)', lineHeight: 3, whiteSpace: 'pre',
        fontFamily: 'var(--fvd-font-body)',
      }}>
        {Array.from({ length: rows }, () => 'Preview · Preview · Preview · Preview · Preview').join('\n')}
      </div>
    </div>
  );

  if (ownedEmpty) {
    return (
      <div>
        <StepHeading variant={variant}>No photos on this link yet</StepHeading>
        <StepText variant={variant}>
          Nothing has been added to this download link so far. Take a selfie to find the photos you&apos;re in.
        </StepText>
        {onRetakeSelfie && (
          <button type="button" onClick={onRetakeSelfie} className="fvd-hoverable" style={stepSecondaryStyle(variant, isMobile)}>
            <Camera size={14} />
            Search for my photos
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      {hasAccess && listError === 'failed' && !loading && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', marginBottom: 14, borderRadius: 'var(--fv-radius-tile, 8px)', background: 'var(--fvd-danger-tint)', color: 'var(--fvd-danger)', fontSize: 13, lineHeight: 1.5 }}>
          <span style={{ flex: 1, minWidth: 0 }}>We couldn&apos;t load your photos just now.</span>
          <button
            type="button"
            onClick={() => setListAttempt((n) => n + 1)}
            className="fvd-hoverable"
            style={stepSecondaryStyle(variant, isMobile)}
          >
            Try again
          </button>
        </div>
      )}
      {/* Header with count and actions */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          {/* With no confident matches but some maybes, "0 photos found" over a
              tray of possibilities reads as a dead end when it is the opposite. */}
          <StepHeading variant={variant} style={{ margin: 0 }}>
            {matchedPhotoIds.length === 0 && possibleMatches.length > 0
              ? 'No exact matches'
              : firstLoad
                ? 'Your photos'
                : hasAccess
                  ? `Your photos (${matchedPhotoIds.length})`
                  : `${matchedPhotoIds.length} photo${matchedPhotoIds.length !== 1 ? 's' : ''} found`}
          </StepHeading>
          <p style={{ fontSize: 13, color: 'var(--fvd-ink-secondary)', margin: '2px 0 0', lineHeight: 1.5 }}>
            {matchedPhotoIds.length === 0 && possibleMatches.length > 0
              ? 'We found some that might be you. Have a look below.'
              : hasAccess
                ? (isMobile ? 'Tap a photo to see it full size.' : 'Click a photo to see it full size.')
                : (isMobile ? 'Tap a photo to preview it.' : 'Click a photo to preview it.')}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {onRetakeSelfie && (
            <button
              type="button"
              onClick={onRetakeSelfie}
              className="fvd-hoverable"
              style={stepSecondaryStyle(variant, isMobile)}
            >
              <Camera size={14} />
              Try another selfie
            </button>
          )}
          {hasAccess && photos.length > 0 && (
            <>
              <button
                type="button"
                onClick={selectAll}
                className="fvd-hoverable"
                style={stepSecondaryStyle(variant, isMobile)}
              >
                {selectedIds.size === matchedPhotoIds.length ? 'Deselect all' : 'Select all'}
              </button>
              <button
                type="button"
                onClick={
                  !willShare
                    ? handleDownloadZip
                    : readyFiles
                      ? shareReadyFiles
                      : prepareShare
                }
                disabled={downloading || startingZip}
                className={!(downloading || startingZip) ? 'fvd-hoverable-solid' : undefined}
                style={hubButtonStyle('primary', isMobile, { height: isMobile ? 44 : 32, padding: '0 14px', fontSize: 12.5, borderRadius: 'var(--fv-radius-button, 10px)', opacity: downloading || startingZip ? 0.5 : 1 })}
              >
                <Download size={16} />
                {downloadButtonLabel({
                  plan,
                  preparing: downloading ? (downloadProgress || 'Preparing...') : null,
                  startingZip,
                  // Only prompt for a second tap when one is really needed.
                  readyCount: awaitingSecondTap ? readyFiles?.length ?? null : null,
                  warming,
                  // Quoted only when we are NOT fetching ahead, so a big set
                  // states its cost before the tap that spends it.
                  sizeHint: !warming && !readyFiles ? formatBytes(totalBytes) : null,
                })}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Whatever went wrong, said out loud. A download that fails in silence
          reads as a broken button, and the only thing left to try is tapping it
          again, which is what people did. */}
      {downloadError && <StepError variant={variant}>{downloadError}</StepError>}

      {/* The share sheet was tried and refused. Whatever we do here, we must
          NOT start the zip ourselves: we are outside the tap that authorised
          anything, and a browser silently blocks a download begun from here.
          That silence is precisely the bug this replaced. So: say what
          happened, and let the next tap do the work. */}
      {shareUnavailable && !downloading && (
        <StepNote>
          <strong style={{ color: 'var(--fvd-ink)', fontWeight: 600 }}>
            {shareUnavailable}
          </strong>
          <br />
          Tap &quot;Download all&quot; and you will get them as a zip file instead
          {isMobile ? ', saved in Files' : ''}.
        </StepNote>
      )}

      {/* A big archive shows nothing until the first byte arrives, and on a
          phone that silence reads as a dead button. This is the only feedback
          available: a navigation download reports no progress back to the
          page, so the page says what to expect instead of pretending to know. */}
      {zipStarted && !readyFiles && (
        <StepNote icon={<Download size={15} />}>
          <strong style={{ color: 'var(--fvd-ink)', fontWeight: 600 }}>
            Preparing your download
          </strong>
          <br />
          {photos.length} photo{photos.length === 1 ? '' : 's'}
          {formatBytes(totalBytes) ? `, about ${formatBytes(totalBytes)}` : ''}. Large sets take a
          moment to start. Your browser will show the download once it begins, and it saves as
          a zip file{isMobile ? ' in Files' : ''}.
        </StepNote>
      )}

      {/* Ready to hand to the share sheet, so the next tap is the one that
          counts. Says so plainly, because a button whose label changed under
          you is otherwise just confusing. */}
      {awaitingSecondTap && readyFiles && !downloading && (
        <p role="status" style={{ fontSize: 13, color: 'var(--fvd-ink-secondary)', lineHeight: 1.5, margin: '0 0 14px' }}>
          Your photos are ready. Tap &quot;Save {readyFiles.length} photo{readyFiles.length === 1 ? '' : 's'}&quot; to add
          them to your camera roll.
        </p>
      )}

      {/* Photo Grid */}
      <div
        aria-busy={loading || undefined}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}
      >
        {firstLoad && Array.from({ length: 6 }).map((_, i) => (
          <div key={`bone-${i}`} aria-hidden="true" style={{ aspectRatio: '4/3' }}>
            <HubSkeleton height={0} radius={8} style={{ height: '100%', borderRadius: 'var(--fv-radius-tile, 8px)' }} />
          </div>
        ))}
        {matchedPhotoIds.map((photoId, index) => {
          const photo = photos.find(p => p.id === photoId);
          const preview = previews.find(p => p.id === photoId);
          const isSelected = selectedIds.has(photoId);
          const imageUrl = photo?.signedUrl || preview?.previewUrl;
          const name = photo?.filename || preview?.filename || 'Race photo';

          return (
            <div
              key={photoId}
              style={{
                position: 'relative', borderRadius: 'var(--fv-radius-tile, 8px)', overflow: 'hidden', aspectRatio: '4/3',
                background: 'var(--fvd-neutral-chip)',
                boxShadow: hasAccess && isSelected ? '0 0 0 2px var(--fvd-teal)' : 'inset 0 0 0 1px var(--fvd-hairline)',
              }}
            >
              {imageUrl ? (
                <>
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(index)}
                    aria-label={`View ${name} full size`}
                    style={{ display: 'block', width: '100%', height: '100%', padding: 0, border: 'none', background: 'none', cursor: 'pointer', lineHeight: 0 }}
                  >
                    <img
                      src={imageUrl}
                      alt={name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </button>
                  {/* Watermark overlay for unpurchased photos */}
                  {!hasAccess && watermark(13, 3)}
                  {hasAccess && photo && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDownloadSingle(photo); }}
                      aria-label={`Download ${name}`}
                      style={{
                        position: 'absolute', bottom: 4, right: 4, width: 40, height: 40,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      }}
                    >
                      <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 'var(--fv-radius-button, 8px)', background: 'rgba(0,0,0,0.6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Download size={15} />
                      </span>
                    </button>
                  )}
                </>
              ) : (
                // The photo is still on its way: a bone the shape of the tile.
                <HubSkeleton height={0} radius={0} style={{ height: '100%' }} />
              )}

              {/* Selection, only when the photos are theirs */}
              {hasAccess && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleSelect(photoId); }}
                  aria-pressed={isSelected}
                  aria-label={isSelected ? `Deselect ${name}` : `Select ${name}`}
                  style={{
                    position: 'absolute', top: 0, right: 0, width: 40, height: 40,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  }}
                >
                  <TileTick on={isSelected}><Check size={14} strokeWidth={2.5} /></TileTick>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Unlock / Download CTA */}
      {!hasAccess && matchedPhotoIds.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <p style={{ fontSize: 13, color: 'var(--fvd-ink-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
            {prePaid
              ? 'Already paid for. Nothing more to pay.'
              : participantPrice === 0
                ? 'Free, no payment needed.'
                : `${formatPrice(participantPrice, participantPriceCurrency)}, includes all ${matchedPhotoIds.length} photo${matchedPhotoIds.length !== 1 ? 's' : ''} in full resolution.`
            }
          </p>
          <button
            type="button"
            onClick={onRequestDownload}
            disabled={unlocking}
            className={!unlocking ? 'fvd-hoverable-solid' : undefined}
            style={hubButtonStyle('primary', isMobile, { width: '100%', borderRadius: 'var(--fv-radius-button, 10px)', opacity: unlocking ? 0.5 : 1, cursor: unlocking ? 'default' : 'pointer' })}
          >
            {unlocking
              ? 'Getting your photos ready...'
              : prePaid || participantPrice === 0
                ? 'Get my photos'
                : `Unlock photos ${formatPrice(participantPrice, participantPriceCurrency)}`
            }
          </button>
        </div>
      )}

      {/* Photo viewer */}
      {lightboxIndex !== null && getImageUrl(lightboxIndex) && (
        <PhotoViewer
          label="Photo viewer"
          index={lightboxIndex}
          count={matchedPhotoIds.length}
          onClose={() => setLightboxIndex(null)}
          onMove={setLightboxIndex}
        >
          <div style={{ position: 'relative', maxWidth: '100%', maxHeight: '100%', display: 'flex', borderRadius: 'var(--fv-radius-tile, 6px)', overflow: 'hidden' }}>
            <img
              src={getImageUrl(lightboxIndex)!}
              alt={getFilename(lightboxIndex)}
              style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 160px)', objectFit: 'contain', display: 'block' }}
            />
            {!hasAccess && watermark(22, 5)}
          </div>
        </PhotoViewer>
      )}

      {possiblePanel}

      {/* Viewer for the maybes. The decision lives in here beside the photo,
          rather than back on a tile the size of a stamp. */}
      {possibleIndex !== null && possibleMatches[possibleIndex] && (() => {
        const photo = possibleMatches[possibleIndex];
        const picked = !!pickedPossible?.has(photo.id);
        return (
          <PhotoViewer
            label="Is this you?"
            index={possibleIndex}
            count={possibleMatches.length}
            onClose={() => setPossibleIndex(null)}
            onMove={setPossibleIndex}
            footer={(
              <button
                type="button"
                onClick={() => onTogglePossible?.(photo.id)}
                aria-pressed={picked}
                style={viewerActionStyle(picked)}
              >
                {picked && <Check size={16} strokeWidth={2.5} />}
                {picked ? 'Added to your photos' : 'This is me'}
              </button>
            )}
          >
            {photo.previewUrl && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={photo.previewUrl}
                alt={photo.filename || 'Possible match'}
                style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 160px)', objectFit: 'contain', borderRadius: 'var(--fv-radius-tile, 6px)', display: 'block' }}
              />
            )}
          </PhotoViewer>
        );
      })()}
    </div>
  );
}

/**
 * A download link the server refused (403). The endpoints deliberately give
 * one answer for every reason (lib/race-photos/event-delivery.ts), so this
 * names the likely ones and what to do about each, instead of "0 photos
 * found": links last DOWNLOAD_TOKEN_TTL_DAYS from when they were issued, and a
 * pre-purchase only opens once the organiser has released the photos.
 */
function DownloadLinkRefused({ variant, isMobile, onSearchAgain }: { variant: PhotoVariant; isMobile: boolean; onSearchAgain?: () => void }) {
  return (
    <div role="alert" style={{ textAlign: 'left' }}>
      <StepHeading variant={variant}>This download link isn&apos;t working</StepHeading>
      <p style={{ fontSize: 13, color: 'var(--fvd-ink-secondary)', lineHeight: 1.55, margin: '0 0 10px', maxWidth: '52ch' }}>
        Download links last {DOWNLOAD_TOKEN_TTL_DAYS} days, so this one may have expired. If you pre-paid for your
        photos, they may not be released yet: you&apos;ll get an email from the race organiser when they are.
      </p>
      <p style={{ fontSize: 13, color: 'var(--fvd-ink-secondary)', lineHeight: 1.55, margin: '0 0 16px', maxWidth: '52ch' }}>
        Paid and still can&apos;t get them? Contact the race organiser with the email address you used.
      </p>
      {onSearchAgain && (
        <button
          type="button"
          onClick={onSearchAgain}
          className="fvd-hoverable"
          style={stepSecondaryStyle(variant, isMobile)}
        >
          <Camera size={14} />
          Search for my photos
        </button>
      )}
    </div>
  );
}
